const QUOTE_FUNDING_KEYS = [
  'budget',
  'prepayment_amount',
  'prepayment_currency',
  'funding_credit',
  'supplier_credit',
  'funding_credit_id',
  'supplier_credit_id',
  'funding_amount',
];

const SENTINEL_BUDGET_VALUES = new Set(['null', 'undefined', 'none', 'n/a']);

/**
 * @param {*} value
 * @returns {string}
 */
function normalizeBudgetText(value) {
  if (value == null) {
    return '';
  }
  const text = String(value).trim();
  if (text === '' || SENTINEL_BUDGET_VALUES.has(text.toLowerCase())) {
    return '';
  }
  return text;
}

/**
 * @param {Object} [patchQuote]
 * @param {Object} [patchResponse]
 * @returns {string}
 */
function resolvePatchBudgetText(patchQuote, patchResponse) {
  return (
    normalizeBudgetText(patchQuote?.budget) ||
    normalizeBudgetText(patchResponse?.budget)
  );
}

/**
 * @param {Object} [patchQuote]
 * @returns {boolean}
 */
function patchIncludesQuoteCreditFunding(patchQuote) {
  if (!patchQuote || typeof patchQuote !== 'object') {
    return false;
  }
  return (
    patchQuote.funding_credit != null ||
    patchQuote.funding_credit_id != null ||
    (patchQuote.funding_amount != null && patchQuote.funding_amount !== '')
  );
}

/**
 * Apply quote funding fields we submitted when PATCH response omits nested quote funding.
 *
 * @param {Object} order
 * @param {{ budget?: *, funding_credit_id?: *, funding_amount?: * }} [fundingFields]
 * @returns {Object}
 */
export function applySubmittedQuoteFundingToOrder(order, fundingFields) {
  if (!order || !fundingFields || typeof fundingFields !== 'object') {
    return order;
  }

  const budgetText = normalizeBudgetText(fundingFields.budget);
  const creditId = fundingFields.funding_credit_id;
  const hasCredit =
    creditId != null && String(creditId).trim() !== '';

  if (budgetText === '' && !hasCredit) {
    return order;
  }

  const existingQuote = order.quote;
  const quoteBase =
    existingQuote != null && typeof existingQuote === 'object'
      ? { ...existingQuote }
      : {
          id:
            typeof existingQuote === 'object' ? existingQuote?.id : existingQuote,
        };

  if (budgetText !== '') {
    return {
      ...order,
      quote: {
        ...quoteBase,
        budget: budgetText,
        funding_credit: null,
        funding_credit_id: null,
        funding_amount: null,
      },
    };
  }

  return {
    ...order,
    quote: {
      ...quoteBase,
      budget: null,
      funding_credit_id: creditId,
      funding_amount: fundingFields.funding_amount ?? null,
      funding_credit: quoteBase.funding_credit ?? creditId,
    },
  };
}

/**
 * Merge PATCH order response into the pre-edit order so list Funding column fields persist.
 * Operational fields come from the patch; funding display fields are kept when omitted.
 *
 * @param {Object|null|undefined} existingOrder - Order before edit (list row or get_details).
 * @param {Object|null|undefined} patchResponse - Order object returned from PATCH.
 * @returns {Object|null} Merged order, or patchResponse if no existing order.
 */
export const mergeOrderAfterUpdate = (existingOrder, patchResponse) => {
  if (!patchResponse) return existingOrder ?? null;
  if (!existingOrder) return patchResponse;

  const merged = { ...existingOrder, ...patchResponse };

  if (
    (patchResponse.supplier_credit == null ||
      patchResponse.supplier_credit === undefined) &&
    existingOrder.supplier_credit != null
  ) {
    merged.supplier_credit = existingOrder.supplier_credit;
  }

  if (
    (patchResponse.amount_charged == null ||
      patchResponse.amount_charged === undefined ||
      patchResponse.amount_charged === '') &&
    existingOrder.amount_charged != null &&
    existingOrder.amount_charged !== ''
  ) {
    merged.amount_charged = existingOrder.amount_charged;
  }

  if (
    (patchResponse.amount_charged_from_credit == null ||
      patchResponse.amount_charged_from_credit === undefined ||
      patchResponse.amount_charged_from_credit === '') &&
    existingOrder.amount_charged_from_credit != null &&
    existingOrder.amount_charged_from_credit !== ''
  ) {
    merged.amount_charged_from_credit = existingOrder.amount_charged_from_credit;
  }

  if (merged.amount_charged == null && merged.amount_charged_from_credit != null) {
    merged.amount_charged = merged.amount_charged_from_credit;
  }

  const existingQuote = existingOrder.quote;
  const patchQuote = patchResponse.quote;
  const patchBudgetText = resolvePatchBudgetText(patchQuote, patchResponse);
  const patchTouchedQuoteFunding =
    patchBudgetText !== '' || patchIncludesQuoteCreditFunding(patchQuote);

  if (existingQuote != null && patchQuote != null) {
    if (typeof patchQuote === 'object' && !Array.isArray(patchQuote)) {
      const quoteMerged = { ...existingQuote, ...patchQuote };

      if (patchBudgetText !== '') {
        quoteMerged.budget = patchBudgetText;
        quoteMerged.funding_credit = null;
        quoteMerged.funding_credit_id = null;
        quoteMerged.funding_amount = null;
      } else if (patchIncludesQuoteCreditFunding(patchQuote)) {
        if (
          patchQuote.budget == null ||
          normalizeBudgetText(patchQuote.budget) === ''
        ) {
          quoteMerged.budget = null;
        }
      } else if (!patchTouchedQuoteFunding) {
        QUOTE_FUNDING_KEYS.forEach(key => {
          const patchVal = patchQuote[key];
          const existingVal = existingQuote[key];
          if (
            (patchVal == null || patchVal === '') &&
            existingVal != null &&
            existingVal !== ''
          ) {
            quoteMerged[key] = existingVal;
          }
        });
      }

      merged.quote = quoteMerged;
      const normalizedBudget = normalizeBudgetText(merged.quote.budget);
      merged.quote.budget = normalizedBudget === '' ? null : normalizedBudget;
    } else {
      const quoteId =
        typeof patchQuote === 'object' ? patchQuote.id : patchQuote;
      const base =
        typeof existingQuote === 'object' && existingQuote != null
          ? { ...existingQuote }
          : {};
      merged.quote = { ...base, id: quoteId ?? base.id };
      if (patchBudgetText !== '') {
        merged.quote.budget = patchBudgetText;
        merged.quote.funding_credit = null;
        merged.quote.funding_credit_id = null;
        merged.quote.funding_amount = null;
      }
    }
  } else if (patchQuote == null && existingQuote != null) {
    merged.quote =
      typeof existingQuote === 'object' ? { ...existingQuote } : existingQuote;
    if (patchBudgetText !== '') {
      merged.quote = {
        ...(typeof existingQuote === 'object'
          ? existingQuote
          : { id: existingQuote }),
        budget: patchBudgetText,
        funding_credit: null,
        funding_credit_id: null,
        funding_amount: null,
      };
    }
  }

  return merged;
};
